import { ReactNode, useEffect, useState } from "react";
import { DocFieldConfig } from "../../models/config/DocFieldConfig";
import { Lowercase } from "../../models/transformation/Lowercase";
import { ReplaceAll } from "../../models/transformation/ReplaceAll";
import { ReplaceDate } from "../../models/transformation/ReplaceDate";
import { ReplaceFirst } from "../../models/transformation/ReplaceFirst";
import { TransformRule } from "../../models/transformation/TransformRule";
import "../../styles/transformation/transformRuleEditor.css";
import { ReplaceDateEditor } from "./ReplaceDateEditor";
import { ReplaceRuleEditor } from "./ReplaceRuleEditor";
import { UpperLowerecaseEditor } from "./UpperLowercaseEditor";

export {
  Props as FieldMatcherEditorProps,
  TransformRuleEditor,
  TransformRuleMatcherEditorChildProps,
};

/**
 * Define props child components may received from the TransformRuleEditor component.
 */
type TransformRuleMatcherEditorChildProps<T extends TransformRule> = {
  display: "view" | "edit";
  /**
   * Child component to display the replaced value.
   */
  // children: React.ReactNode;
  rule: Readonly<T>;
  /**
   * Called when the TransformRule has been updated. A null value represents the rule being deleted.
   * @param rule
   */
  update: (rule: T | null) => void;
  changedRuleType?: (rule: TransformRule) => void;
  /**
   * Add a button component to the TransformRuleEditor.
   */
  button?: ReactNode;
  /**
   * Text to display in TransformRuleEditor label
   */
  label?: string;
  /**
   * onClick handler for TransformRuleEditor label
   */
  onClickLabel?(): void;
  index?: number;
  /**
   * Possible DocFieldConfigs to suggest
   */
  fields: DocFieldConfig[];
  /**
   * Variables for regex suggestion
   */
  variables: string[];
};

// TODO: refactor types in this class to align with MatcherEditor
type Props = {
  display: "edit" | "view";
  rule: Readonly<TransformRule>;
  /**
   * Called when the TransformRule has been updated. A null value represents the rule being deleted.
   * @param rule A new TransformRule instance.
   */
  update: (rule: TransformRule | null, oldRuleId: string) => void;
  changedRuleType?: (rule: TransformRule | null, oldRuleId: string) => void;
} & Omit<
  TransformRuleMatcherEditorChildProps<TransformRule>,
  "update" | "changedRuleType"
>;

const TransformRuleEditor = (props: Props) => {
  const [oldRuleId, setOldRuleId] = useState<string>(props.rule.getRuleId());
  //  update oldRuleId when rule changes
  useEffect(() => setOldRuleId(props.rule.getRuleId()), [props.rule]);

  const childProps = {
    ...props,
  };

  const update = (rule: TransformRule | null) => props.update(rule, oldRuleId);

  const changedRuleType = props.changedRuleType
    ? (rule: TransformRule) => props.changedRuleType!(rule, oldRuleId)
    : undefined;

  switch (props.rule.getDiscriminator()) {
    case "T_REPALL": {
      return (
        <ReplaceRuleEditor
          {...childProps}
          rule={props.rule as ReplaceAll}
          changedRuleType={changedRuleType}
          update={update}
        />
      );
    }
    case "T_REPFIRST": {
      return (
        <ReplaceRuleEditor
          {...childProps}
          rule={props.rule as ReplaceFirst}
          changedRuleType={changedRuleType}
          update={update}
        />
      );
    }
    case "T_LCASE": {
      return (
        <UpperLowerecaseEditor
          {...childProps}
          rule={props.rule as Lowercase}
          changedRuleType={changedRuleType}
          update={update}
        />
      );
    }
    case "T_UCASE": {
      return (
        <UpperLowerecaseEditor
          {...childProps}
          rule={props.rule as Lowercase}
          changedRuleType={changedRuleType}
          update={update}
        />
      );
    }
    case "T_REPDATE": {
      return (
        <ReplaceDateEditor
          {...childProps}
          rule={props.rule as ReplaceDate}
          changedRuleType={changedRuleType}
          update={update}
        />
      );
    }
    default: {
      return null;
    }
  }
};
